import { useState } from 'react';
import { Download, Loader2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FormatType, formats } from './FormatSelector';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface GeneratedImage {
  format: FormatType;
  url: string;
}

interface OutputPanelProps {
  images: GeneratedImage[];
  isLoading: boolean;
  loadingFormats: FormatType[];
  hideTitle?: boolean;
}

export function OutputPanel({ images, isLoading, loadingFormats, hideTitle }: OutputPanelProps) {
  const [expandedImages, setExpandedImages] = useState<Set<string>>(new Set());
  const isMultipleImages = images.length > 1;

  // Initialize all images as expanded when they're first added
  const getIsExpanded = (key: string) => {
    if (!isMultipleImages) return true;
    // Default to expanded if not in the set yet
    return !expandedImages.has(key) || expandedImages.has(key);
  };

  const toggleExpanded = (key: string) => {
    setExpandedImages((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Track collapsed state (inverted logic - track what's collapsed)
  const [collapsedImages, setCollapsedImages] = useState<Set<string>>(new Set());

  const isExpanded = (key: string) => !collapsedImages.has(key);

  const handleToggle = (key: string) => {
    setCollapsedImages((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleDownload = async (url: string, format: FormatType) => {
    const formatInfo = formats.find(f => f.id === format);
    const link = document.createElement('a');
    link.href = url;
    link.download = `scalemage-${format}-${formatInfo?.dimensions || ''}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderImageCard = (image: GeneratedImage, index: number) => {
    const formatInfo = formats.find(f => f.id === image.format);
    const key = `${image.format}-${index}`;

    const header = (
      <div className={cn(
        "p-3 border-b border-border flex items-center justify-between",
        isMultipleImages && "cursor-pointer hover:bg-secondary/50 transition-colors"
      )}>
        <div className="flex items-center gap-2">
          {isMultipleImages && (
            <ChevronDown 
              className={cn(
                "h-4 w-4 transition-transform duration-200",
                !isExpanded(key) && "-rotate-90"
              )} 
            />
          )}
          <span className="font-medium">{formatInfo?.label}</span>
          <span className="text-xs text-muted-foreground">
            {formatInfo?.dimensions}
          </span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            handleDownload(image.url, image.format);
          }}
        >
          <Download className="h-4 w-4 mr-2" />
          Download
        </Button>
      </div>
    );

    const imageContent = (
      <div className="p-2">
        <div
          className="w-full overflow-hidden rounded-lg"
          style={{ aspectRatio: formatInfo?.aspectRatio?.replace(':', ' / ') }}
        >
          <img
            src={image.url}
            alt={`Generated ${formatInfo?.label}`}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
      </div>
    );

    if (!isMultipleImages) {
      return (
        <div
          key={key}
          className="bg-secondary/30 rounded-xl overflow-hidden"
        >
          {header}
          {imageContent}
        </div>
      );
    }

    return (
      <Collapsible
        key={key}
        open={isExpanded(key)}
        onOpenChange={() => handleToggle(key)}
        className="bg-secondary/30 rounded-xl overflow-hidden"
      >
        <CollapsibleTrigger asChild>
          {header}
        </CollapsibleTrigger>
        <CollapsibleContent>
          {imageContent}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {!hideTitle && <h2 className="text-lg font-semibold mb-4">Output</h2>}
      
      {images.length === 0 && !isLoading ? (
        <div className="flex-1 flex items-center justify-center border-2 border-dashed border-border rounded-xl bg-secondary/20">
          <p className="text-muted-foreground text-center">
            Generated images will appear here
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {loadingFormats.map((format) => (
            <div
              key={`loading-${format}`}
              className="bg-secondary/30 rounded-xl p-4 flex items-center justify-center aspect-video"
            >
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">
                  Generating {format}...
                </p>
              </div>
            </div>
          ))}
          
          {images.map((image, index) => renderImageCard(image, index))}
        </div>
      )}
    </div>
  );
}
